import React, { useState, useEffect } from 'react';
import { Plus, Edit, X } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import { useNotification } from '../../hooks/useNotification';
import Modal from '../UI/Modal';
import Input from '../UI/Input';
import Button from '../UI/Button';
import { ExtraServiceCreateData, ExtraServiceUpdateData, BillingTransaction } from '../../services/billingService';

interface ExtraServiceFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ExtraServiceCreateData | ExtraServiceUpdateData) => Promise<void>;
  mode: 'add' | 'edit';
  initialData?: BillingTransaction | null;
  eventId?: string;
}

const ExtraServiceForm: React.FC<ExtraServiceFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  initialData,
  eventId,
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { showNotification } = useNotification();
  const [serviceName, setServiceName] = useState('');
  const [amount, setAmount] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        setServiceName(initialData.service_name || '');
        setAmount(Math.abs(parseFloat(initialData.amount)).toFixed(2));
      } else {
        setServiceName('');
        setAmount('');
      }
      setErrors({});
    }
  }, [isOpen, mode, initialData]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!serviceName.trim()) {
      newErrors.serviceName = 'Service name is required';
    }

    const amountNum = parseFloat(amount);
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      if (mode === 'add') {
        if (!eventId) {
          showNotification('Event ID is required', 'error');
          return;
        }
        await onSubmit({
          event_id: eventId,
          service_name: serviceName.trim(),
          amount: amount,
        });
      } else {
        await onSubmit({
          service_name: serviceName.trim(),
          amount: amount,
        });
      }
      onClose();
    } catch (error: any) {
      showNotification(
        error.response?.data?.error?.message || 'Failed to save extra service',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add Extra Service' : 'Edit Extra Service'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            type="text"
            placeholder="Service name"
            value={serviceName}
            onChange={(e) => {
              setServiceName(e.target.value);
              if (errors.serviceName) {
                setErrors((prev) => ({ ...prev, serviceName: '' }));
              }
            }}
            error={errors.serviceName}
            label="Service Name *"
            required
            autoFocus
          />
        </div>

        <div>
          <Input
            type="number"
            step="0.01"
            min="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              if (errors.amount) {
                setErrors((prev) => ({ ...prev, amount: '' }));
              }
            }}
            error={errors.amount}
            label="Price (JOD) *"
            required
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={loading}
            leftIcon={mode === 'add' ? <Plus className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
          >
            {mode === 'add' ? 'Add Service' : 'Update Service'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ExtraServiceForm;

